import {CODE_PERMISSIONS, CodePermissionKey} from './permissions';

/**
 * Code-defined map of key pages / core functionality → the permission keys
 * they require. This is documentation-grade metadata consumed by the
 * "Features" tab of the internal roles & permissions hub: it answers
 * "what does a role need to reach this feature?" for admins editing roles
 * and for QA verifying role parity. It is NOT an enforcement layer — the
 * actual gates live in the routers/components listed alongside each entry.
 *
 * Keep entries in sync when a gate changes: if a router or component starts
 * or stops requiring a permission, update the matching feature here.
 */
export type FeatureRequirementArea =
  | 'Internal portal'
  | 'Client portal'
  | 'Group portal'
  | 'Broker portal';

export type FeatureRequirement = {
  /** Stable identifier, `<area>.<feature>` snake_case. */
  readonly key: string;
  readonly name: string;
  readonly area: FeatureRequirementArea;
  readonly description: string;
  /** Every listed permission is required (AND). */
  readonly requiredPermissions: readonly CodePermissionKey[];
  /** At least one of these is required (OR), when applicable. */
  readonly anyOfPermissions?: readonly CodePermissionKey[];
  /** Non-permission requirement, e.g. the profile type the gate also checks. */
  readonly profileNote?: string;
};

/**
 * Ordered list — the UI renders in this order, grouped by `area`.
 */
export const FEATURE_REQUIREMENTS: readonly FeatureRequirement[] = [
  // ---------------------------------------------------------------------
  // Internal portal
  // ---------------------------------------------------------------------
  {
    key: 'internal.claims_tracking',
    name: 'Claims Tracking dashboard',
    area: 'Internal portal',
    description: 'Consolidated claims tracking dashboards (sidebar item and page).',
    requiredPermissions: [CODE_PERMISSIONS.claimsTracking.view.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.audit',
    name: 'Activity log',
    area: 'Internal portal',
    description:
      'The cross-tenant activity log at /audit/activity. Per-entity History tabs are not gated by this.',
    requiredPermissions: [CODE_PERMISSIONS.audit.view.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.cbs_queue',
    name: 'CBS Queue',
    area: 'Internal portal',
    description:
      'Ready-for-CBS RFC queue page, sidebar item, queue widgets on RFC lists, and driving the CBS implementation checklist (submit to CBS / begin implementation / advance stage).',
    requiredPermissions: [CODE_PERMISSIONS.rfc.cbsQueue.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.roles_permissions_admin',
    name: 'Roles & permissions administration',
    area: 'Internal portal',
    description: 'The /permissions hub: role catalog, permission catalog, and this features view.',
    requiredPermissions: [],
    anyOfPermissions: [CODE_PERMISSIONS.roles.manage.key, CODE_PERMISSIONS.permissions.manage.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.cross_tenant_access',
    name: 'Cross-tenant data access',
    area: 'Internal portal',
    description:
      'Access groups, clients, accounts, RFCs, and prospects across all tenants regardless of profile membership.',
    requiredPermissions: [CODE_PERMISSIONS.tenant.accessAll.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.client_feature_flags',
    name: 'Client feature flags',
    area: 'Internal portal',
    description: 'Edit per-tenant feature flags and portal configuration on the Clients console.',
    requiredPermissions: [CODE_PERMISSIONS.clients.manageFeatures.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.internal_features_config',
    name: 'Internal features configuration',
    area: 'Internal portal',
    description: 'Edit org-wide internal portal feature configuration.',
    requiredPermissions: [CODE_PERMISSIONS.internalConfig.manage.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.carrier_reconciliation',
    name: 'Carrier invoice reconciliation',
    area: 'Internal portal',
    description: 'Upload carrier invoices, run reconciliation, and resolve billing discrepancies.',
    requiredPermissions: [CODE_PERMISSIONS.carrierReconciliation.manage.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.onboarding_dry_run',
    name: 'Onboarding dry-run steps',
    area: 'Internal portal',
    description:
      'Add or modify dry-run steps on onboarding templates. Also controls the add/edit/delete step controls in the onboarding template editor.',
    requiredPermissions: [CODE_PERMISSIONS.onboarding.dryRun.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.change_user_roles',
    name: 'Change user roles',
    area: 'Internal portal',
    description:
      'Reassign a user\u2019s role from the user detail and client pages. Reassigning INTERNAL-scoped roles additionally requires the role-management permission.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.editUserRole.key],
    profileNote: 'Requires an internal profile; internal-role changes also require roles.manage',
  },
  {
    key: 'internal.external_api_users',
    name: 'External API users',
    area: 'Internal portal',
    description: 'Create, list, and deactivate external (server-to-server) API users.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.manageExternalUsers.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.payroll_debug',
    name: 'Payroll batch debug info',
    area: 'Internal portal',
    description:
      'View the debug panel on payroll upload results (batch details plus CBS file key, file check status, and Skynet record counts).',
    requiredPermissions: [CODE_PERMISSIONS.payroll.debug.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.payroll_test_upload_override',
    name: 'Payroll test-upload override',
    area: 'Internal portal',
    description:
      'Submit payroll uploads flagged as tests (group name is prefixed with TEST_ in CBS). Also controls the "This is a test upload" checkbox on the payroll upload page.',
    requiredPermissions: [CODE_PERMISSIONS.payroll.overrideTestUpload.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.rfc_edit_processing',
    name: 'Edit RFCs in CBS processing',
    area: 'Internal portal',
    description: 'Edit RFCs while they are in CBS_PROCESSING status.',
    requiredPermissions: [CODE_PERMISSIONS.rfc.editProcessing.key],
    profileNote: 'Requires an internal profile',
  },
  {
    key: 'internal.manage_internal_tickets',
    name: 'Internal ticket queue',
    area: 'Internal portal',
    description:
      'Work the CBS-side portal ticket queue (list, review, status, engineering routing).',
    requiredPermissions: [CODE_PERMISSIONS.tickets.manageInternal.key],
    profileNote: 'Requires an internal profile',
  },

  // ---------------------------------------------------------------------
  // Client portal
  // ---------------------------------------------------------------------
  {
    key: 'client.accounts_admin',
    name: 'Accounts admin actions',
    area: 'Client portal',
    description:
      'Admin actions on accounts: link VBA group, manage SWOT reviews, transfer ownership, skip sub-stages, edit assigned broker.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.admin.key],
    profileNote: 'Requires a client profile (internal users need tenant.access_all instead)',
  },
  {
    key: 'client.prospects',
    name: 'Prospects pipeline',
    area: 'Client portal',
    description: 'View and work the prospects pipeline.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewProspects.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.rfc_queue',
    name: 'RFC queue',
    area: 'Client portal',
    description: 'RFC submissions list, proposal generator routes, and the RFC sidebar entry.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewRfcQueue.key],
    profileNote: 'Requires a client profile (brokers have their own route)',
  },
  {
    key: 'client.page_dashboard',
    name: 'Dashboard page',
    area: 'Client portal',
    description:
      'Main dashboard landing page. Not yet enforced — the dashboard renders for every client user regardless of this key.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewDashboard.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.page_accounts',
    name: 'Accounts pages',
    area: 'Client portal',
    description: 'Accounts list, kanban, and detail pages (sidebar entry + list endpoint).',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewAccounts.key],
    profileNote: 'Gates client-profile users only; brokers and internal users pass',
  },
  {
    key: 'client.page_pipeline',
    name: 'Pipeline page',
    area: 'Client portal',
    description: 'RFC pipeline sidebar entry and views.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewPipeline.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.page_members',
    name: 'Member pages',
    area: 'Client portal',
    description: 'Member search sidebar entry and the member search endpoint.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewMembers.key],
    profileNote: 'Gates client-profile users only; group users pass',
  },
  {
    key: 'client.page_brokers',
    name: 'Broker pages',
    area: 'Client portal',
    description: 'Broker tree/list sidebar entries and broker list endpoints.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewBrokers.key],
    profileNote: 'Gates client-profile users only; internal users pass',
  },
  {
    key: 'client.page_documents',
    name: 'Documents page',
    area: 'Client portal',
    description: 'Broker document compliance sidebar entry and list endpoint.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewDocuments.key],
    profileNote: 'Gates client-profile users only; internal users pass',
  },
  {
    key: 'client.page_resources',
    name: 'Resources page',
    area: 'Client portal',
    description: 'Resource library admin sidebar entry and list endpoint.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewResources.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.page_training',
    name: 'Training pages',
    area: 'Client portal',
    description: 'Training admin and pitch-training sidebar entries and course list endpoint.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewTraining.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.page_communications',
    name: 'Message center page',
    area: 'Client portal',
    description: 'Broker message center sidebar entry and message list endpoint.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewCommunications.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.page_quick_proposal',
    name: 'Quick Proposal tool',
    area: 'Client portal',
    description: 'Fission Quick Proposal embed (proposal generator and account proposal tabs).',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewQuickProposal.key],
    profileNote: 'Gates client-profile users only; brokers and internal users pass',
  },
  {
    key: 'client.page_informed_analysis',
    name: 'Informed Analysis tool',
    area: 'Client portal',
    description: 'Fission Informed Analysis embed on accounts.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewInformedAnalysis.key],
    profileNote: 'Gates client-profile users only; brokers and internal users pass',
  },
  {
    key: 'client.page_user_management',
    name: 'User management page',
    area: 'Client portal',
    description: 'Users sidebar entry, user list, and pending registrations list.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewUserManagement.key],
    profileNote: 'Gates client-profile users only; internal users pass',
  },
  {
    key: 'client.page_imports',
    name: 'Imports page',
    area: 'Client portal',
    description: 'Broker import sidebar entry.',
    requiredPermissions: [CODE_PERMISSIONS.pageAccess.viewImports.key],
    profileNote: 'Gates client-profile users only',
  },
  {
    key: 'client.rfc_adjudication',
    name: 'RFC adjudication (approve / request corrections)',
    area: 'Client portal',
    description:
      'The Review Actions card on an RFC in SUBMITTED / CLIENT_REVIEW / BROKER_APPROVED status. Open to every client and group user by design \u2014 no permission required.',
    requiredPermissions: [],
    profileNote: 'Any non-internal, non-broker viewer',
  },
  {
    key: 'client.rfc_admin_workflow',
    name: 'RFC admin workflow controls',
    area: 'Client portal',
    description:
      'Admin review pipeline cards and inline-edit controls on RFC pages (distinct from adjudication).',
    requiredPermissions: [CODE_PERMISSIONS.workflow.approveRfc.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.rfc_pepm',
    name: 'RFC PEPM rates',
    area: 'Client portal',
    description:
      'View PEPM rate details on RFCs. The API strips the rate from RFC payloads for non-holders.',
    requiredPermissions: [CODE_PERMISSIONS.rfc.viewPepm.key],
    profileNote: 'Requires a client profile (internal users always see PEPM)',
  },
  {
    key: 'client.rfc_send_msa',
    name: 'Send MSA (DocuSign or manual)',
    area: 'Client portal',
    description:
      'Send the MSA for an approved deal via DocuSign or record a manual MSA submission (RFC review + account MSA tab).',
    requiredPermissions: [CODE_PERMISSIONS.workflow.sendMsaViaDocusign.key],
    profileNote: 'Internal users always pass',
  },
  {
    key: 'client.rfc_notify_cbs',
    name: 'Submit deal to CBS',
    area: 'Client portal',
    description: 'Trigger the CBS handoff once the MSA is signed.',
    requiredPermissions: [CODE_PERMISSIONS.workflow.approveSendToCbs.key],
    profileNote: 'Internal users always pass',
  },
  {
    key: 'client.rfc_manage_notes',
    name: "Edit other users' RFC notes",
    area: 'Client portal',
    description:
      "Edit or delete another user's RFC note. Adding notes and editing your own stays open to every RFC viewer.",
    requiredPermissions: [CODE_PERMISSIONS.workflow.editRfcInternalNotes.key],
    profileNote: 'Internal users always pass; note authors can always edit their own',
  },
  {
    key: 'client.member_full_ssn',
    name: 'Member full SSN',
    area: 'Client portal',
    description:
      'Reveal the full (unmasked) SSN on member search, member details, and CSV exports. The API pre-masks the SSN to last-4 for non-holders.',
    requiredPermissions: [CODE_PERMISSIONS.phi.viewMemberSsnFull.key],
  },
  {
    key: 'client.member_dob',
    name: 'Member date of birth',
    area: 'Client portal',
    description:
      'See member dates of birth on member search, member details, and CSV exports. The API omits DOB for non-holders.',
    requiredPermissions: [CODE_PERMISSIONS.phi.viewMemberDob.key],
  },
  {
    key: 'client.member_add',
    name: 'Add members',
    area: 'Client portal',
    description:
      'Create members/subscribers via the add-member wizard or the eligibility file import (Import File on member search).',
    requiredPermissions: [CODE_PERMISSIONS.members.add.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.member_edit',
    name: 'Edit member records',
    area: 'Client portal',
    description:
      'Edit member/subscriber details (contact info, SSN, DOB, external ID, identity) from group member pages.',
    requiredPermissions: [CODE_PERMISSIONS.members.edit.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.member_edit_coverage',
    name: 'Edit member coverage',
    area: 'Client portal',
    description: 'Add or terminate products and change coverage tiers on a member.',
    requiredPermissions: [CODE_PERMISSIONS.members.editCoverage.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.member_edit_dependents',
    name: 'Edit member dependents',
    area: 'Client portal',
    description: 'Add or drop dependents on a member record.',
    requiredPermissions: [CODE_PERMISSIONS.members.editDependents.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.member_terminate',
    name: 'Terminate / reinstate members',
    area: 'Client portal',
    description: "Terminate or reinstate a member's coverage from the member profile.",
    requiredPermissions: [CODE_PERMISSIONS.members.terminate.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.group_edit',
    name: 'Edit group fields',
    area: 'Client portal',
    description:
      'Edit group fields (name, address, contact, FEIN, billing cycle) and manage group plans/commission agents.',
    requiredPermissions: [CODE_PERMISSIONS.groups.edit.key],
    profileNote: 'Enforced for client-profile callers and internal portal users',
  },
  {
    key: 'client.member_export',
    name: 'Member CSV export',
    area: 'Client portal',
    description: 'Export the member search results to CSV.',
    requiredPermissions: [CODE_PERMISSIONS.members.exportCsv.key],
    profileNote: 'Enforced for client-profile callers; group users and internal users pass',
  },
  {
    key: 'client.account_create',
    name: 'Create accounts',
    area: 'Client portal',
    description: 'Create a new account/group in the pipeline.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.create.key],
    profileNote: 'Enforced for client-profile callers; brokers and internal users pass',
  },
  {
    key: 'client.account_edit',
    name: 'Edit account details',
    area: 'Client portal',
    description: 'Edit basic company/account fields on the account detail page.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.editBasic.key],
    profileNote: 'Enforced for client-profile callers; brokers and internal users pass',
  },
  {
    key: 'client.account_move_stage',
    name: 'Move account pipeline stage',
    area: 'Client portal',
    description: 'Manually transition an account between pipeline phases.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.movePipelineStageManual.key],
    profileNote: 'Enforced for client-profile callers; brokers and internal users pass',
  },
  {
    key: 'client.account_archive',
    name: 'Archive / unarchive accounts',
    area: 'Client portal',
    description: 'Archive an account (soft-delete) or restore an archived one.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.archive.key],
    profileNote: 'Enforced for client-profile callers; brokers and internal users pass',
  },
  {
    key: 'client.account_export',
    name: 'Account CSV export',
    area: 'Client portal',
    description: 'Export the accounts list to CSV.',
    requiredPermissions: [CODE_PERMISSIONS.accounts.export.key],
    profileNote: 'Enforced for client-profile callers; brokers and internal users pass',
  },
  {
    key: 'client.broker_add',
    name: 'Add brokers',
    area: 'Client portal',
    description: 'Create brokers individually or via CSV import.',
    requiredPermissions: [CODE_PERMISSIONS.brokers.add.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.broker_edit_profile',
    name: 'Edit broker profiles',
    area: 'Client portal',
    description: 'Edit broker contact, commission, and license info.',
    requiredPermissions: [CODE_PERMISSIONS.brokers.editProfile.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.broker_edit_tree',
    name: 'Edit broker tree',
    area: 'Client portal',
    description:
      'Move brokers within the upline/downline hierarchy, via drag-and-drop or the "Move Under..." action on the Brokers page.',
    requiredPermissions: [CODE_PERMISSIONS.brokers.editTree.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.broker_archive',
    name: 'Archive brokers',
    area: 'Client portal',
    description:
      'Archive (deactivate) and reactivate brokers, including the Archive/Reactivate buttons on the broker detail page.',
    requiredPermissions: [CODE_PERMISSIONS.brokers.archive.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.user_invite',
    name: 'Invite users',
    area: 'Client portal',
    description:
      'Invite client/group users and brokers (creates pending registrations) and resend invites.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.inviteUser.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.user_edit',
    name: 'Edit user basics',
    area: 'Client portal',
    description: 'Edit user name, email, feature flags, and group access.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.editUserBasic.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.user_archive',
    name: 'Archive / unarchive users',
    area: 'Client portal',
    description: 'Archive a user (soft-delete, expires sessions) or restore them.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.archiveUser.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.registration_codes_generate',
    name: 'Regenerate registration codes',
    area: 'Client portal',
    description: 'Resend a pending registration email (rotates the signup code).',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.generateRegistrationCodes.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.registration_codes_revoke',
    name: 'Revoke registration codes',
    area: 'Client portal',
    description: 'Cancel a pending registration, invalidating its signup code.',
    requiredPermissions: [CODE_PERMISSIONS.userManagement.revokeRegistrationCodes.key],
    profileNote: 'Enforced for client-profile callers; internal users pass',
  },
  {
    key: 'client.broadcast_send',
    name: 'Send broadcast messages',
    area: 'Client portal',
    description: 'Send or schedule broadcast announcements to brokers from the message center.',
    requiredPermissions: [CODE_PERMISSIONS.governance.sendBroadcastAnnouncement.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.payroll_processing',
    name: 'Payroll processing',
    area: 'Client portal',
    description: 'Access payroll processing for groups within the client tenant.',
    requiredPermissions: [CODE_PERMISSIONS.payroll.clientAccess.key],
    profileNote: 'Requires a client profile (group users have their own access path)',
  },
  {
    key: 'client.vip_management',
    name: 'VIP member management',
    area: 'Client portal',
    description:
      'Manage VIP members for the tenant (page and sidebar entry). Client users without this do not see VIP members in member search results.',
    requiredPermissions: [CODE_PERMISSIONS.vip.manage.key],
    profileNote: 'Requires a client profile (any internal user also passes)',
  },
  {
    key: 'client.view_as_broker',
    name: 'View portal as broker',
    area: 'Client portal',
    description: 'Preview the broker portal as one of the tenant\u2019s brokers (read-only).',
    requiredPermissions: [CODE_PERMISSIONS.brokers.viewAs.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.sensitive_member_fields',
    name: 'Sensitive member fields',
    area: 'Client portal',
    description:
      'Admin-level group access: sensitive member fields such as SSN, DOB, and external IDs, plus member edit affordances.',
    requiredPermissions: [CODE_PERMISSIONS.groups.adminAccess.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.ticket_triage',
    name: 'Ticket triage',
    area: 'Client portal',
    description:
      'Triage portal tickets as a tenant admin (assign, comment, resolve). Client users without this cannot use ticketing and do not see the Tickets nav item.',
    requiredPermissions: [CODE_PERMISSIONS.tickets.triage.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.ticket_escalation_review',
    name: 'Ticket escalation review',
    area: 'Client portal',
    description: 'Review the dev-request escalation queue (send back to triage, forward to CBS).',
    requiredPermissions: [CODE_PERMISSIONS.tickets.escalationReview.key],
    profileNote: 'Requires a client profile',
  },
  {
    key: 'client.admin_notifications',
    name: 'Client-admin notifications',
    area: 'Client portal',
    description: 'Receive and manage client-admin-level notifications.',
    requiredPermissions: [CODE_PERMISSIONS.notifications.clientAdminLevel.key],
    profileNote: 'Requires a client profile',
  },

  // ---------------------------------------------------------------------
  // Group portal / eligibility
  // ---------------------------------------------------------------------
  {
    key: 'group.eligibility_view',
    name: 'Eligibility (view)',
    area: 'Group portal',
    description: 'View member eligibility data.',
    requiredPermissions: [CODE_PERMISSIONS.eligibility.view.key],
  },
  {
    key: 'group.eligibility_update',
    name: 'Eligibility (update)',
    area: 'Group portal',
    description: 'Update member eligibility data.',
    requiredPermissions: [CODE_PERMISSIONS.eligibility.update.key],
  },
  {
    key: 'group.admin_notifications',
    name: 'Group-admin notifications',
    area: 'Group portal',
    description: 'Receive and manage group-admin-level notifications.',
    requiredPermissions: [CODE_PERMISSIONS.notifications.groupAdminLevel.key],
    profileNote: 'Requires a group profile',
  },
];

/** Canonical area display order for grouped rendering. */
export const FEATURE_AREAS: readonly FeatureRequirementArea[] = [
  'Internal portal',
  'Client portal',
  'Group portal',
  'Broker portal',
];

/**
 * Whether a role holding `rolePermissions` satisfies a feature's permission
 * requirements (ALL of `requiredPermissions` plus, when present, at least one
 * of `anyOfPermissions`). Profile-type requirements (`profileNote`) are not
 * evaluated here — they depend on the user, not the role.
 */
export function roleSatisfiesFeature(
  feature: FeatureRequirement,
  rolePermissions: ReadonlySet<string>,
): boolean {
  const hasAllRequired = feature.requiredPermissions.every((key) => rolePermissions.has(key));
  const hasAnyOf =
    !feature.anyOfPermissions ||
    feature.anyOfPermissions.length === 0 ||
    feature.anyOfPermissions.some((key) => rolePermissions.has(key));
  return hasAllRequired && hasAnyOf;
}
